/**
 * Billing API Client
 * Talks to the payment-service backend using JWT from auth-service
 */

import { services, apiBase, isProxied } from '../proxy-config';
const BILLING_API = isProxied ? '' : (process.env.REACT_APP_BILLING_API_URL || 'http://localhost:5020');
const AUTH_API = services.auth.backend;

class BillingApiClient {
  getToken() {
    return localStorage.getItem('billing-access-token');
  }

  async request(path, options = {}) {
    const url = `${BILLING_API}${path}`;
    const headers = { 'Content-Type': 'application/json', ...options.headers };

    const token = this.getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(url, { ...options, headers });

    // Auto-refresh on 401
    if (response.status === 401 && !options._isRetry) {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        return this.request(path, { ...options, _isRetry: true });
      }
      // Redirect to auth login
      window.location.href = `${AUTH_API.replace(/\/api$/, '')}/login?redirect=${encodeURIComponent(window.location.href)}`;
      throw new Error('Authentication required');
    }

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const error = new Error(data.error || data.message || 'Request failed');
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return data;
  }

  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem('billing-refresh-token');
      if (!refreshToken) return false;

      const response = await fetch(`${AUTH_API}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });

      if (!response.ok) return false;

      const data = await response.json();
      localStorage.setItem('billing-access-token', data.tokens.accessToken);
      if (data.tokens.refreshToken) {
        localStorage.setItem('billing-refresh-token', data.tokens.refreshToken);
      }
      return true;
    } catch {
      return false;
    }
  }

  get(path) { return this.request(path); }
  post(path, body) { return this.request(path, { method: 'POST', body: JSON.stringify(body) }); }
  put(path, body) { return this.request(path, { method: 'PUT', body: JSON.stringify(body) }); }
  del(path) { return this.request(path, { method: 'DELETE' }); }
}

const billingApi = new BillingApiClient();
export default billingApi;
